import { BaseRestRepository } from '~/repositories/baseRestRepository';
import { validateId } from '~/repositories/utils';
import { companiesServiceUrl } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import type {
  AbandonBusinessInfoDraftRequestDto,
  AttachBusinessInfoDraftDocumentRequestDto,
  BusinessInfoDraftDto,
  BusinessInfoDraftPrincipalRequestDto,
  BusinessInfoItemDto,
  BusinessInfoPhase,
  BusinessInfosDto,
  InitBusinessInfoDraftRequestDto,
  PatchBusinessInfoDraftRequestDto,
  RequestUploadSessionDto,
  UploadSessionDto,
  WithdrawBusinessInfoDraftRequestDto,
} from '~/swagger/companies/Api';

const businessInfoBaseUrl = () => `${companiesServiceUrl()}/company-portal/business-info`;

export class CompanyBusinessInfoRepository extends BaseRestRepository {
  getUploadSessionUrl = (businessInfoId: string): string => {
    validateId(businessInfoId, 'Business info');
    return `${businessInfoBaseUrl()}/${businessInfoId}/upload-sessions`;
  };

  getBusinessInfoList = async (phases?: BusinessInfoPhase[]): Promise<BusinessInfoItemDto[]> => {
    const url = urlWithQuery(businessInfoBaseUrl(), { phases });
    const response = await this.fetchWithAuth(url, {}, false);
    const data: BusinessInfosDto = await this.getJsonResponse(response);
    return data.items;
  };

  initDraft = async (data: InitBusinessInfoDraftRequestDto): Promise<BusinessInfoDraftDto> => {
    const response = await this.fetchWithAuth(businessInfoBaseUrl(), { method: 'POST', body: JSON.stringify(data) });
    return this.getJsonResponse(response);
  };

  getDraft = async (businessInfoId: string): Promise<BusinessInfoDraftDto> => {
    validateId(businessInfoId, 'Business info');
    const response = await this.fetchWithAuth(`${businessInfoBaseUrl()}/${businessInfoId}`);
    return this.getJsonResponse(response);
  };

  startUpdateDraft = async (businessInfoId: string): Promise<BusinessInfoDraftDto> => {
    validateId(businessInfoId, 'Business info');
    const response = await this.fetchWithAuth(`${businessInfoBaseUrl()}/${businessInfoId}`, { method: 'POST' });
    return this.getJsonResponse(response);
  };

  patchDraft = async (businessInfoId: string, data: PatchBusinessInfoDraftRequestDto): Promise<BusinessInfoDraftDto> => {
    validateId(businessInfoId, 'Business info');
    const response = await this.fetchWithAuth(`${businessInfoBaseUrl()}/${businessInfoId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return this.getJsonResponse(response);
  };

  submitDraft = async (businessInfoId: string, showErrorMessage = true): Promise<BusinessInfoDraftDto> => {
    validateId(businessInfoId, 'Business info');
    const response = await this.fetchWithAuth(`${businessInfoBaseUrl()}/${businessInfoId}/submit`, { method: 'POST' }, showErrorMessage);
    return this.getJsonResponse(response);
  };

  withdrawDraft = async (businessInfoId: string, data?: WithdrawBusinessInfoDraftRequestDto): Promise<BusinessInfoDraftDto> => {
    validateId(businessInfoId, 'Business info');
    const response = await this.fetchWithAuth(`${businessInfoBaseUrl()}/${businessInfoId}/withdraw`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
    return this.getJsonResponse(response);
  };

  abandonDraft = async (businessInfoId: string, data?: AbandonBusinessInfoDraftRequestDto): Promise<void> => {
    validateId(businessInfoId, 'Business info');
    await this.fetchWithAuth(`${businessInfoBaseUrl()}/${businessInfoId}/abandon`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  };

  setPrimary = async (businessInfoId: string): Promise<BusinessInfoItemDto> => {
    validateId(businessInfoId, 'Business info');
    const response = await this.fetchWithAuth(`${businessInfoBaseUrl()}/${businessInfoId}/primary`, { method: 'PUT' });
    return this.getJsonResponse(response);
  };

  createUploadSession = async (businessInfoId: string, data: RequestUploadSessionDto): Promise<UploadSessionDto> => {
    validateId(businessInfoId, 'Business info');
    const response = await this.fetchWithAuth(this.getUploadSessionUrl(businessInfoId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this.getJsonResponse(response);
  };

  attachDocument = async (businessInfoId: string, data: AttachBusinessInfoDraftDocumentRequestDto): Promise<BusinessInfoDraftDto> => {
    validateId(businessInfoId, 'Business info');
    const response = await this.fetchWithAuth(`${businessInfoBaseUrl()}/${businessInfoId}/documents`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this.getJsonResponse(response);
  };

  deleteDocument = async (businessInfoId: string, documentId: string): Promise<BusinessInfoDraftDto> => {
    validateId(businessInfoId, 'Business info');
    validateId(documentId, 'Document');
    const response = await this.fetchWithAuth(`${businessInfoBaseUrl()}/${businessInfoId}/documents/${documentId}`, {
      method: 'DELETE',
    });
    return this.getJsonResponse(response);
  };

  addPrincipal = async (businessInfoId: string, data: BusinessInfoDraftPrincipalRequestDto): Promise<BusinessInfoDraftDto> => {
    validateId(businessInfoId, 'Business info');
    const response = await this.fetchWithAuth(`${businessInfoBaseUrl()}/${businessInfoId}/principals`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this.getJsonResponse(response);
  };

  patchPrincipal = async (
    businessInfoId: string,
    principalId: string,
    data: BusinessInfoDraftPrincipalRequestDto
  ): Promise<BusinessInfoDraftDto> => {
    validateId(businessInfoId, 'Business info');
    validateId(principalId, 'Principal');
    const response = await this.fetchWithAuth(`${businessInfoBaseUrl()}/${businessInfoId}/principals/${principalId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return this.getJsonResponse(response);
  };

  deletePrincipal = async (businessInfoId: string, principalId: string): Promise<BusinessInfoDraftDto> => {
    validateId(businessInfoId, 'Business info');
    validateId(principalId, 'Principal');
    const response = await this.fetchWithAuth(`${businessInfoBaseUrl()}/${businessInfoId}/principals/${principalId}`, {
      method: 'DELETE',
    });
    return this.getJsonResponse(response);
  };
}

export const companyBusinessInfoRepository = new CompanyBusinessInfoRepository();
